// Enable once RaiseWarning/ RaiseError rule execution emits these events.
export const ENABLE_RAISED_RULE_EVENTS = false;

export type EventType = keyof typeof EVENT_TYPE_INFO;

export interface EventTypeInfo {
  id: EventType;
  name: string;
  description: string;
  category:
    | "release"
    | "feedback"
    | "contact"
    | "session"
    | "orgmodels"
    | "task"
    | "integration"
    | "assignment"
    | "communication"
    | "usage"
    | "expiration"
    | "trigger"
    | "job"
    | "fileProcessing"
    | "user";
  canConnect: boolean;
  /** Highlights this type in connector setup; all types can still be subscribed to. */
  recommendedConnectable: boolean;
  legacyTypes: string[];
  factInfo: EventFactTypeInfo[];
}

/** "debug" events (routine lifecycle records such as job started/completed) are hidden from event lists by default. */
export type EventStatus = "debug" | "info" | "warning" | "error" | "success";

export interface EventFact {
  url?: string;
  key: string;
  value: unknown;
}

/** Legacy input is accepted during rollout; normalized/stored facts only have key, value and url. */
export type EventFactInput = { key?: string; value: unknown; url?: string; label?: string; type?: string };

export interface EventFactTypeInfo {
  key: string;
  label: string;
  valueType: "string" | "number";
  exampleValue: string | number;
}

const USER_FACT_INFO: EventFactTypeInfo[] = [
  { key: "user", label: "User", valueType: "string", exampleValue: "alex@example.com" },
  { key: "identity-id", label: "Identity ID", valueType: "string", exampleValue: "00000000-0000-0000-0000-000000000000" },
  { key: "linked", label: "Linked", valueType: "string", exampleValue: "false" },
];

const FILE_PROCESSING_FACT_INFO: EventFactTypeInfo[] = [
  { key: "scope", label: "Scope", valueType: "string", exampleValue: "contacts" },
  { key: "file", label: "File", valueType: "string", exampleValue: "contacts.csv" },
  { key: "file-path", label: "File path", valueType: "string", exampleValue: "workspace-id/project-id/contacts/processed/contacts.csv" },
  { key: "project", label: "Project", valueType: "string", exampleValue: "Contact Import" },
  { key: "release", label: "Release", valueType: "string", exampleValue: "Release 23" },
  { key: "mapping", label: "Mapping", valueType: "string", exampleValue: "Contact Import Mapping" },
  { key: "rule", label: "Rule", valueType: "string", exampleValue: "Import contacts" },
  { key: "reprocessed-from", label: "Reprocessed from", valueType: "string", exampleValue: "workspace-id/project-id/contacts/error/contacts.errors.csv" },
  { key: "requested-by", label: "Reprocess requested by", valueType: "string", exampleValue: "Alex Smith" },
];

/** Facts on contact events: where the write came from, why, and the path an @parent update wrote to. */
const CONTACT_FACT_INFO: EventFactTypeInfo[] = [
  { key: "source", label: "Source", valueType: "string", exampleValue: "fileProcessing" },
  { key: "reason", label: "Reason", valueType: "string", exampleValue: "ledger-update" },
  { key: "path", label: "Path", valueType: "string", exampleValue: "jobs/01/occupancies/X" },
];

const CONNECTION_CALL_FACT_INFO: EventFactTypeInfo[] = [
  { key: "connection", label: "Connection", valueType: "string", exampleValue: "Payroll SFTP" },
  { key: "connection-id", label: "Connection ID", valueType: "string", exampleValue: "connection-123" },
  { key: "connection-type", label: "Connection type", valueType: "string", exampleValue: "sftp" },
  { key: "source", label: "Source", valueType: "string", exampleValue: "delivery" },
  { key: "method", label: "Method", valueType: "string", exampleValue: "UPLOAD" },
  { key: "status", label: "Status", valueType: "string", exampleValue: "succeeded" },
  { key: "file", label: "File", valueType: "string", exampleValue: "payrun.csv" },
  { key: "duration-ms", label: "Duration", valueType: "number", exampleValue: 120 },
  { key: "size-bytes", label: "Size", valueType: "number", exampleValue: 2048 },
  { key: "status-code", label: "Status code", valueType: "number", exampleValue: 200 },
  { key: "remote-path", label: "Remote path", valueType: "string", exampleValue: "/outbound/payrun.csv" },
  { key: "receiver-reference", label: "Receiver reference", valueType: "string", exampleValue: "receipt-123" },
  { key: "failure-reason", label: "Failure reason", valueType: "string", exampleValue: "timeout" },
  { key: "retryable", label: "Retryable", valueType: "string", exampleValue: "true" },
];

export const getEventFactTypeInfo = (key: string, eventType?: string): EventFactTypeInfo | undefined => {
  const facts = eventType ? getEventTypeInfo(eventType)?.factInfo || [] : Object.values(EVENT_TYPE_INFO).flatMap(info => info.factInfo);
  const info = facts.find(info => info.key === key);
  if (info) return info;
  const family = facts.find(info => info.key.endsWith(":*") && key.startsWith(info.key.slice(0, -1)));
  return family ? { ...family, key, label: family.label.replace("{id}", key.slice(family.key.length - 1)) } : undefined;
};

export const getEventFactLabel = (fact: EventFactInput, eventType?: string): string => {
  const key = fact.key || toFactKey(fact.label || "Value", "value");
  return getEventFactTypeInfo(key, eventType)?.label || fact.label || key.replace(/[-_]/g, " ").replace(/^./, c => c.toUpperCase());
};

export const getEventTypeDisplayName = (eventType?: string): string => {
  const info = getEventTypeInfo(eventType);
  if (info) return info.name;
  const customEventType = typeof eventType === "string" ? eventType.trim() : "";
  return customEventType ? `${customEventType} (Custom Event)` : "Unknown event";
};

export interface EventAction {
  label: string;
  url: string;
}

export interface EventAttachment {
  filename: string;
  content: string;
  type: string;
  size?: number;
}

export interface EventNotification {
  body: string;
  severity?: EventStatus;
  category?: string;
  facts?: EventFactInput[];
  actions?: EventAction[];
  attachments?: EventAttachment[];
  footer?: string;
  rawData?: unknown;
}

/**
 * How an event's `meta` is stored: "diff" is a jsondiffpatch delta (old and new values of the changed fields),
 * "normal" is plain data.
 */
export type EventMetaFormat = "diff" | "normal";

export interface Event {
  notification: EventNotification;
  id?: string;
  eventRowKey?: string;
  taskId?: string;
  requestId?: string;
  eventType?: EventType | (string & {});
  timestamp?: string;
  message?: string;
  resolution?: { resolved: boolean; status?: import("./index").TaskStatus; taskId?: string; updatedAt?: string; updatedBy?: string };
  sessionId?: string;
  modelId?: string;
  releaseId?: string;
  correlationId?: string;
  status?: EventStatus;
  facts?: EventFact[];
  meta?: Record<string, any>;
  metaFormat?: EventMetaFormat;
  identity?: Record<string, any>;
}

export const EVENT_TYPE_INFO = {
  "release.create": {
    factInfo: [],
    id: "release.create" as const,
    name: "Release created",
    description: "Sent when a new release is created in any project in this tenancy.",
    category: "release",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["release.create"],
  },
  "feedback.submitted": {
    factInfo: [
      { key: "feedback-id", label: "Feedback ID", valueType: "string", exampleValue: "feedback-123" },
      { key: "url", label: "URL", valueType: "string", exampleValue: "https://portal.example.com/projects/project-123" },
      { key: "user", label: "User", valueType: "string", exampleValue: "Alex Smith alex@example.com" },
      { key: "session", label: "Session", valueType: "string", exampleValue: "session-123" },
      { key: "workspace", label: "Workspace", valueType: "string", exampleValue: "workspace-123" },
      { key: "project", label: "Project", valueType: "string", exampleValue: "project-123" },
      { key: "release", label: "Release", valueType: "string", exampleValue: "release-123" },
    ],
    id: "feedback.submitted" as const,
    name: "Feedback submitted",
    description: "Sent when a user submits feedback. Large artifacts are routed by connector type.",
    category: "feedback",
    canConnect: true,
    recommendedConnectable: true,
    legacyTypes: ["feedback.submitted"],
  },
  "contactIdentifier.created": {
    factInfo: [],
    id: "contactIdentifier.created" as const,
    name: "Contact identifier created",
    description: "Recorded when contact identifiers are created.",
    category: "contact",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["create_contact_identifier"],
  },
  "contactIdentifier.updated": {
    factInfo: [],
    id: "contactIdentifier.updated" as const,
    name: "Contact identifier updated",
    description: "Recorded when contact identifiers are updated.",
    category: "contact",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["update_contact_identifier"],
  },
  "contactIdentifier.deleted": {
    factInfo: [],
    id: "contactIdentifier.deleted" as const,
    name: "Contact identifier deleted",
    description: "Recorded when contact identifiers are deleted.",
    category: "contact",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["delete_contact_identifier"],
  },
  "contactIdentifier.purged": {
    factInfo: [],
    id: "contactIdentifier.purged" as const,
    name: "Contact identifiers purged",
    description: "Recorded when contact identifiers are purged.",
    category: "contact",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["purge_contact_identifiers"],
  },
  "contact.created": {
    factInfo: CONTACT_FACT_INFO,
    id: "contact.created" as const,
    name: "Contact created",
    description: "Recorded when a contact record is created.",
    category: "contact",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "contact.updated": {
    factInfo: CONTACT_FACT_INFO,
    id: "contact.updated" as const,
    name: "Contact updated",
    description: "Recorded when a contact record is updated.",
    category: "contact",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["update"],
  },
  "contact.deleted": {
    factInfo: CONTACT_FACT_INFO,
    id: "contact.deleted" as const,
    name: "Contact deleted",
    description: "Recorded when a contact record is deleted.",
    category: "contact",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "user.created": {
    factInfo: USER_FACT_INFO,
    id: "user.created" as const,
    name: "User created",
    description: "Recorded when a user is added to the tenancy. Linked users already existed in the identity provider.",
    category: "user",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "user.updated": {
    factInfo: USER_FACT_INFO,
    id: "user.updated" as const,
    name: "User updated",
    description: "Recorded when a user's details or roles are updated.",
    category: "user",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "user.deleted": {
    factInfo: USER_FACT_INFO,
    id: "user.deleted" as const,
    name: "User deleted",
    description: "Recorded when a user is removed from the tenancy.",
    category: "user",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "session.failedInteraction": {
    factInfo: [
      { key: "session", label: "Session", valueType: "string", exampleValue: "session-123" },
      { key: "interaction", label: "Interaction", valueType: "string", exampleValue: "interaction-123" },
      { key: "type", label: "Type", valueType: "string", exampleValue: "outbound" },
      { key: "error", label: "Error", valueType: "string", exampleValue: "Request timed out" },
      { key: "method", label: "Method", valueType: "string", exampleValue: "POST" },
      { key: "model", label: "Model", valueType: "string", exampleValue: "project-123" },
      { key: "release", label: "Release", valueType: "string", exampleValue: "release-123" },
      { key: "trigger", label: "Trigger", valueType: "string", exampleValue: "Send confirmation email" },
    ],
    id: "session.failedInteraction" as const,
    name: "Session failed interaction",
    description: "Sent when a session interaction fails, such as integration failures or processing errors.",
    category: "session",
    canConnect: true,
    recommendedConnectable: true,
    legacyTypes: ["session.failedInteraction"],
  },
  "session.raiseWarning": {
    factInfo: [],
    id: "session.raiseWarning" as const,
    name: "Raise warning",
    description: "A warning raised by a RaiseWarning rule during session evaluation.",
    category: "session",
    canConnect: ENABLE_RAISED_RULE_EVENTS,
    recommendedConnectable: ENABLE_RAISED_RULE_EVENTS,
    legacyTypes: [],
  },
  "session.raiseError": {
    factInfo: [],
    id: "session.raiseError" as const,
    name: "Raise error",
    description: "An error raised by a RaiseError rule during session evaluation.",
    category: "session",
    canConnect: ENABLE_RAISED_RULE_EVENTS,
    recommendedConnectable: ENABLE_RAISED_RULE_EVENTS,
    legacyTypes: [],
  },
  "session.error": {
    factInfo: [],
    id: "session.error" as const,
    name: "Session error",
    description: "Sent when a session transitions into an error state.",
    category: "session",
    canConnect: true,
    recommendedConnectable: true,
    legacyTypes: ["session.error"],
  },
  "session.dataAdded": {
    factInfo: [],
    id: "session.dataAdded" as const,
    name: "Session data added",
    description: "Recorded when input data is added to a session.",
    category: "session",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["session_data"],
  },
  "orgmodels.error": {
    factInfo: [
      { key: "subject-type", label: "Subject type", valueType: "string", exampleValue: "position" },
      { key: "subject-id", label: "Subject ID", valueType: "string", exampleValue: "position-123" },
      { key: "issue", label: "Issue", valueType: "string", exampleValue: "hierarchy-cycle" },
      { key: "cycle", label: "Cycle", valueType: "string", exampleValue: "position-123 -> position-456 -> position-123" },
      { key: "issue-count", label: "Issue count", valueType: "number", exampleValue: 2 },
      { key: "cycle-count", label: "Cycle count", valueType: "number", exampleValue: 1 },
      { key: "cycle:*", label: "Cycle {id}", valueType: "string", exampleValue: "position-123 -> position-456 -> position-123" },
    ],
    id: "orgmodels.error" as const,
    name: "Org model error",
    description: "Sent when org model processing detects a hierarchy issue such as a parent cycle.",
    category: "orgmodels",
    canConnect: true,
    recommendedConnectable: true,
    legacyTypes: ["orgmodels.error"],
  },
  "session.triggersChecked": {
    factInfo: [],
    id: "session.triggersChecked" as const,
    name: "Session triggers checked",
    description: "Recorded when scheduled session triggers are checked for a tenancy.",
    category: "session",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["session_triggers"],
  },
  "session.scheduledTriggersActivated": {
    factInfo: [],
    id: "session.scheduledTriggersActivated" as const,
    name: "Scheduled session triggers activated",
    description: "Recorded when active scheduled triggers are found for a session.",
    category: "session",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["scheduled_trigger"],
  },
  "task.escalationChecked": {
    factInfo: [],
    id: "task.escalationChecked" as const,
    name: "Task escalation checked",
    description: "Recorded when tasks due for escalation are checked for a tenancy.",
    category: "task",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["task_escalation"],
  },
  "task.escalationScheduled": {
    factInfo: [],
    id: "task.escalationScheduled" as const,
    name: "Task escalation scheduled",
    description: "Recorded when a task escalation time is calculated and scheduled.",
    category: "task",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["escalation_scheduled"],
  },
  "task.escalationRescheduled": {
    factInfo: [],
    id: "task.escalationRescheduled" as const,
    name: "Task escalation rescheduled",
    description: "Recorded when an existing task escalation is moved to a new time.",
    category: "task",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["task_escalation_rescheduled"],
  },
  "task.escalationAssigneesResolved": {
    factInfo: [],
    id: "task.escalationAssigneesResolved" as const,
    name: "Task escalation assignees resolved",
    description: "Recorded when escalation resolves assignees for a task.",
    category: "task",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["task_escalation_assignees"],
  },
  "task.escalationCommunicationSent": {
    factInfo: [],
    id: "task.escalationCommunicationSent" as const,
    name: "Task escalation communication sent",
    description: "Recorded when an escalation communication is sent.",
    category: "task",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["task_escalation_communication"],
  },
  "task.escalationRemoved": {
    factInfo: [],
    id: "task.escalationRemoved" as const,
    name: "Task escalation removed",
    description: "Recorded when escalation is removed from a task.",
    category: "task",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["task_escalation_removed"],
  },
  "task.escalationUpdated": {
    factInfo: [],
    id: "task.escalationUpdated" as const,
    name: "Task escalation updated",
    description: "Recorded when a task is updated by escalation processing.",
    category: "task",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["task_escalation_update"],
  },
  "task.escalationError": {
    factInfo: [],
    id: "task.escalationError" as const,
    name: "Task escalation error",
    description: "Recorded when task escalation processing fails.",
    category: "task",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["task_escalation_error"],
  },
  "task.escalationAssignmentError": {
    factInfo: [],
    id: "task.escalationAssignmentError" as const,
    name: "Task escalation assignment error",
    description: "Recorded when escalation cannot resolve task assignees.",
    category: "task",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["task_escalation_assignment_error"],
  },
  "integration.callFailed": {
    factInfo: [
      { key: "requestUrl", label: "Request URL", valueType: "string", exampleValue: "https://api.example.com/contacts" },
    ],
    id: "integration.callFailed" as const,
    name: "Integration call failed",
    description: "Recorded when an integration API call fails before a successful response is returned.",
    category: "integration",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "integration.callSucceeded": {
    factInfo: [
      { key: "requestUrl", label: "Request URL", valueType: "string", exampleValue: "https://api.example.com/contacts" },
    ],
    id: "integration.callSucceeded" as const,
    name: "Integration call succeeded",
    description: "Recorded when an integration API call completes successfully.",
    category: "integration",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["integration_call"],
  },
  "integration.callbackSucceeded": {
    factInfo: [],
    id: "integration.callbackSucceeded" as const,
    name: "Integration callback succeeded",
    description: "Recorded when an integration response mapping successfully calls back into Decisively.",
    category: "integration",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["integration_call_back"],
  },
  "connection.callStarted": {
    factInfo: CONNECTION_CALL_FACT_INFO,
    id: "connection.callStarted" as const,
    name: "Connection call started",
    description: "Recorded when a configured release connection call starts.",
    category: "integration",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "connection.callFailed": {
    factInfo: CONNECTION_CALL_FACT_INFO,
    id: "connection.callFailed" as const,
    name: "Connection call failed",
    description: "Recorded when a configured release connection call fails.",
    category: "integration",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "connection.callSucceeded": {
    factInfo: CONNECTION_CALL_FACT_INFO,
    id: "connection.callSucceeded" as const,
    name: "Connection call succeeded",
    description: "Recorded when a configured release connection call completes successfully.",
    category: "integration",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "assignment.integrationStarted": {
    factInfo: [],
    id: "assignment.integrationStarted" as const,
    name: "Assignment integration started",
    description: "Recorded when assignment resolution starts by calling an integration.",
    category: "assignment",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["assignment_integration_start"],
  },
  "assignment.mappingError": {
    factInfo: [],
    id: "assignment.mappingError" as const,
    name: "Assignment mapping error",
    description: "Recorded when an assignment integration response cannot be mapped.",
    category: "assignment",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["assignment_mapping_error"],
  },
  "assignment.validationError": {
    factInfo: [],
    id: "assignment.validationError" as const,
    name: "Assignment validation error",
    description: "Recorded when an assignment integration response has the wrong shape.",
    category: "assignment",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["assignment_validation_error"],
  },
  "assignment.noContacts": {
    factInfo: [],
    id: "assignment.noContacts" as const,
    name: "Assignment returned no contacts",
    description: "Recorded when an assignment integration returns no contact IDs.",
    category: "assignment",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["assignment_no_contacts"],
  },
  "assignment.noValidContacts": {
    factInfo: [],
    id: "assignment.noValidContacts" as const,
    name: "Assignment returned no valid contacts",
    description: "Recorded when returned assignment contact IDs cannot be validated.",
    category: "assignment",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["assignment_no_valid_contacts"],
  },
  "assignment.sessionUpdated": {
    factInfo: [],
    id: "assignment.sessionUpdated" as const,
    name: "Assignment session updated",
    description: "Recorded when assignment resolution updates the session with mapped data.",
    category: "assignment",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["assignment_session_updated"],
  },
  "assignment.integrationError": {
    factInfo: [],
    id: "assignment.integrationError" as const,
    name: "Assignment integration error",
    description: "Recorded when an assignment integration call fails.",
    category: "assignment",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["assignment_integration_error"],
  },
  "assignment.fallbackFailed": {
    factInfo: [],
    id: "assignment.fallbackFailed" as const,
    name: "Assignment fallback failed",
    description: "Recorded when assignment fallback to default delegates cannot resolve assignees.",
    category: "assignment",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["assignment_fallback_failed"],
  },
  "assignment.fallbackSucceeded": {
    factInfo: [],
    id: "assignment.fallbackSucceeded" as const,
    name: "Assignment fallback succeeded",
    description: "Recorded when assignment fallback resolves assignees from default delegates.",
    category: "assignment",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["assignment_fallback_success"],
  },
  "assignment.completed": {
    factInfo: [],
    id: "assignment.completed" as const,
    name: "Assignment completed",
    description: "Recorded when assignment resolution finishes with or without assignees.",
    category: "assignment",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["assignment_success"],
  },
  "communication.sent": {
    factInfo: [],
    id: "communication.sent" as const,
    name: "Communication sent",
    description: "Recorded when a communication trigger sends successfully.",
    category: "communication",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["comms_send"],
  },
  "usage.eventStarted": {
    factInfo: [],
    id: "usage.eventStarted" as const,
    name: "Usage event started",
    description: "Recorded when usage event processing starts.",
    category: "usage",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["usageEvent-start"],
  },
  "usage.eventCompleted": {
    factInfo: [],
    id: "usage.eventCompleted" as const,
    name: "Usage event completed",
    description: "Recorded when usage event processing completes.",
    category: "usage",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["usageEvent-end"],
  },
  "expiration.started": {
    factInfo: [],
    id: "expiration.started" as const,
    name: "Expiration started",
    description: "Recorded when expiration processing starts.",
    category: "expiration",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["expiration-start"],
  },
  "expiration.completed": {
    factInfo: [],
    id: "expiration.completed" as const,
    name: "Expiration completed",
    description: "Recorded when expiration processing completes.",
    category: "expiration",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["expiration-end"],
  },
  "trigger.idempotencySkipped": {
    factInfo: [],
    id: "trigger.idempotencySkipped" as const,
    name: "Duplicate trigger skipped",
    description: "Recorded when duplicate trigger work is skipped by idempotency protection.",
    category: "trigger",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["trigger_idempotency"],
  },
  "job.started": {
    factInfo: [],
    id: "job.started" as const,
    name: "Job started",
    description: "Recorded by the generic job runner when a queued job starts processing.",
    category: "job",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "job.completed": {
    factInfo: [],
    id: "job.completed" as const,
    name: "Job completed",
    description: "Recorded by the generic job runner when a queued job completes processing.",
    category: "job",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "job.failed": {
    factInfo: [],
    id: "job.failed" as const,
    name: "Job failed",
    description: "Recorded by the generic job runner when a queued job fails processing.",
    category: "job",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "fileProcessing.call": {
    factInfo: [
      ...FILE_PROCESSING_FACT_INFO,
    ],
    id: "fileProcessing.call" as const,
    name: "File processing mapping selected",
    description: "Event recorded when file processing resolves the mapping and rule for a file.",
    category: "fileProcessing",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["file_processing_call"],
  },
  "fileProcessing.started": {
    factInfo: [
      ...FILE_PROCESSING_FACT_INFO,
    ],
    id: "fileProcessing.started" as const,
    name: "File processing started",
    description: "Event recorded when file processing starts handling a file.",
    category: "fileProcessing",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: [],
  },
  "fileProcessing.succeeded": {
    factInfo: [
      ...FILE_PROCESSING_FACT_INFO,
      { key: "processed-path", label: "Processed path", valueType: "string", exampleValue: "workspace-id/project-id/contacts/processed/contacts.csv" },
    ],
    id: "fileProcessing.succeeded" as const,
    name: "File processing succeeded",
    description: "Event recorded when file processing finishes successfully without row errors.",
    category: "fileProcessing",
    canConnect: true,
    recommendedConnectable: true,
    legacyTypes: [],
  },
  "fileProcessing.failedRows": {
    factInfo: [
      ...FILE_PROCESSING_FACT_INFO,
      { key: "processed-path", label: "Processed path", valueType: "string", exampleValue: "workspace-id/project-id/contacts/processed/contacts.csv" },
      { key: "error-path", label: "Error path", valueType: "string", exampleValue: "workspace-id/project-id/contacts/error/contacts.csv" },
      { key: "csv-error-path", label: "Error CSV path", valueType: "string", exampleValue: "workspace-id/project-id/contacts/error/contacts.errors.csv" },
      { key: "failedRows", label: "Failed rows", valueType: "number", exampleValue: 3 },
      { key: "row:*", label: "Row {id}", valueType: "string", exampleValue: "row 3: Unable to parse email for column 'WorkEmail': expected a valid email address" },
    ],
    id: "fileProcessing.failedRows" as const,
    name: "File processing rows failed",
    description: "Event recorded when rows fail validation or processing, independently of the final file move.",
    category: "fileProcessing",
    canConnect: true,
    recommendedConnectable: true,
    legacyTypes: ["fileProcessing.partial"],
  },
  "fileProcessing.failed": {
    factInfo: [
      ...FILE_PROCESSING_FACT_INFO,
      { key: "error-path", label: "Error path", valueType: "string", exampleValue: "workspace-id/project-id/contacts/error/contacts.csv" },
      { key: "csv-error-path", label: "Error CSV path", valueType: "string", exampleValue: "workspace-id/project-id/contacts/error/contacts.errors.csv" },
    ],
    id: "fileProcessing.failed" as const,
    name: "File processing failed",
    description: "Event recorded when file processing fails before it can complete the file.",
    category: "fileProcessing",
    canConnect: true,
    recommendedConnectable: true,
    legacyTypes: [],
  },
  "fileProcessing.finished": {
    factInfo: [
      ...FILE_PROCESSING_FACT_INFO,
    ],
    id: "fileProcessing.finished" as const,
    name: "File processing finished",
    description: "Event recorded when file processing stops handling a file, regardless of success or failure.",
    category: "fileProcessing",
    canConnect: true,
    recommendedConnectable: false,
    legacyTypes: ["fileProcessing.completed"],
  },
} satisfies Record<string, Omit<EventTypeInfo, "id"> & { id: string }>;

export const CONNECTABLE_EVENT_TYPES = Object.values(EVENT_TYPE_INFO).filter((event) => event.canConnect);

export const CONNECTABLE_EVENT_TYPE_IDS = CONNECTABLE_EVENT_TYPES.map((event) => event.id);

export const LEGACY_EVENT_TYPE_TO_UNIFIED_EVENT_TYPE = Object.values(EVENT_TYPE_INFO).reduce<Record<string, EventType>>(
  (acc, event) => {
    acc[event.id] = event.id;
    for (const legacyType of event.legacyTypes) {
      acc[legacyType] = event.id;
      acc[legacyType.toLowerCase()] = event.id;
    }
    return acc;
  },
  {},
);

export const getEventType = (rawType: string | undefined | null): EventType | undefined => {
  if (!rawType) return undefined;
  return (
    LEGACY_EVENT_TYPE_TO_UNIFIED_EVENT_TYPE[rawType] ||
    LEGACY_EVENT_TYPE_TO_UNIFIED_EVENT_TYPE[String(rawType).toLowerCase()]
  );
};

export const getEventTypeInfo = (rawType: string | undefined | null): EventTypeInfo | undefined => {
  const unified = getEventType(rawType);
  return unified ? EVENT_TYPE_INFO[unified] : undefined;
};

const parseEventObject = (value: unknown): Record<string, any> | undefined => {
  if (!value) return undefined;
  if (typeof value === "object") return value as Record<string, any>;
  if (typeof value !== "string") return undefined;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, any>) : undefined;
  } catch {
    return undefined;
  }
};

const getEventObjectCandidates = (event: Record<string, any> | undefined | null): Record<string, any>[] => {
  const meta = parseEventObject(event?.meta);
  const payload = parseEventObject(event?.payload);
  const data = parseEventObject(event?.data);
  const rawData = parseEventObject(event?.rawData);
  const message = parseEventObject(event?.message);
  return [event, meta, payload, data, rawData, message, message?.payload, message?._job?.payload].filter(
    Boolean,
  ) as Record<string, any>[];
};

const toFactKey = (value: string, fallback: string): string => {
  const key = String(value || fallback)
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return key || fallback;
};

const normalizeStatus = (event: Record<string, any> | undefined | null): EventStatus | undefined => {
  const value = String(event?.status || event?.severity || "").toLowerCase();
  if (["error", "failed", "failure"].includes(value)) return "error";
  if (["warning", "warn", "partial"].includes(value)) return "warning";
  if (["success", "succeeded", "complete", "completed"].includes(value)) return "success";
  if (value === "info") return "info";
  if (value === "debug") return "debug";
  return undefined;
};

const isMessageFact = (fact: { key?: string; label?: string } | null | undefined): boolean =>
  Boolean(fact && [fact.key, fact.label].some(value => value?.trim().toLowerCase() === "message"));

/** Only web URLs and application-relative paths may be opened from event facts. */
export const getEventFactUrl = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const url = value.trim();
  if (!url || /[\\\s\u0000-\u001f]/.test(url)) return undefined;
  if (url.startsWith("/") && !url.startsWith("//")) return url;
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : undefined;
  } catch {
    return undefined;
  }
};

export const normalizeEventFacts = (
  facts?: EventFactInput[] | Array<Partial<EventFact> & { label?: string; key?: string; value?: unknown; type?: string }> | null,
): EventFact[] | undefined => {
  const result: EventFact[] = [];
  for (const fact of facts || []) {
    if (!fact || typeof fact !== "object") continue;
    if (isMessageFact(fact) || [fact.key, fact.label].some(value => toFactKey(value || "", "") === "request-id")) continue;
    // Legacy reference facts sometimes contain only storage IDs. Keep those in
    // the raw event/metadata instead of presenting them as human-readable facts.
    const reference = [fact.key, fact.label, fact.type].some(value =>
      /^(project|model|session|release|mapping|rule)([ _-]?id)?$/i.test(String(value || "")));
    if (reference && typeof fact.value === "string" && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(fact.value)) continue;
    const label = String(fact.label || fact.key || "Value");
    const rawKey = String(fact.key || toFactKey(label, `fact-${result.length + 1}`));
    const key = ({ "failed-rows": "failedRows", "request-url": "requestUrl", sessionId: "session" } as Record<string, string>)[rawKey] || rawKey;
    result.push({
      key,
      value: fact.value,
      ...(fact.url ? { url: fact.url } : {}),
    });
  }
  for (const fact of result) {
    const session = ["session", "sessionid"].includes(fact.key.toLowerCase());
    const url = getEventFactUrl(fact.url)
      || (typeof fact.value === "string" && /^https?:\/\//i.test(fact.value) ? getEventFactUrl(fact.value) : undefined)
      || (session && typeof fact.value === "string" && fact.value ? `/monitor/overview/${encodeURIComponent(fact.value)}` : undefined);
    if (url) fact.url = url;
    else delete fact.url;
  }
  return result.length ? result : undefined;
};

const getCandidateObjects = (event: Record<string, any> | undefined | null): Record<string, any>[] =>
  getEventObjectCandidates(event);

const getCandidateNumber = (event: Record<string, any> | undefined | null, key: string): number => {
  for (const candidate of getCandidateObjects(event)) {
    const value = candidate[key];
    const parsed = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const hasCandidateString = (event: Record<string, any> | undefined | null, key: string): boolean =>
  getCandidateObjects(event).some(
    (candidate) => typeof candidate[key] === "string" && candidate[key].trim().length > 0,
  );

const getFileProcessingOutcomeEventType = (event: Record<string, any> | undefined | null): EventType => {
  const statuses = getCandidateObjects(event).map((candidate) => String(candidate.status || "").toLowerCase());
  if (statuses.some((status) => ["error", "failed", "failure"].includes(status))) return "fileProcessing.failed";
  if (getCandidateNumber(event, "csvFailedRows") > 0 || hasCandidateString(event, "csvErrorPath"))
    return "fileProcessing.failedRows";
  return "fileProcessing.succeeded";
};

export const getRawEventType = (event: Record<string, any> | undefined | null): string | undefined => {
  for (const candidate of getEventObjectCandidates(event)) {
    const raw =
      candidate.eventType || candidate.messageType || candidate.event || candidate._job?.jobType || candidate.jobType;
    if (typeof raw === "string" && raw.trim()) return raw;
  }
  return undefined;
};

export const getEventModelId = (event: Record<string, any> | undefined | null): string | undefined => {
  for (const candidate of getEventObjectCandidates(event)) {
    const direct = candidate.modelId || candidate.projectId || candidate.model;
    if (typeof direct === "string" && direct.trim()) return direct;
  }
  return undefined;
};

export const getEventSessionId = (event: Record<string, any> | undefined | null): string | undefined => {
  for (const candidate of getEventObjectCandidates(event)) {
    const id = candidate.sessionId || candidate.session;
    if (typeof id === "string" && id.trim()) return id;
  }
  const type = getEventType(getRawEventType(event));
  if (
    type?.startsWith("session.") ||
    type?.startsWith("assignment.") ||
    type === "integration.callSucceeded" ||
    type === "integration.callFailed"
  ) {
    const id = event?.correlationId;
    if (typeof id === "string" && id.trim() && id !== "system") return id;
  }
  return undefined;
};

const getStatusAwareEventType = (
  event: Record<string, any> | undefined | null,
  rawEventType: string | undefined,
): EventType | string | undefined => {
  const raw = String(rawEventType || "").toLowerCase();
  if (raw === "fileprocessing.outcome" || raw === "file_processing_outcome")
    return getFileProcessingOutcomeEventType(event);
  if (raw.endsWith("-start")) return "job.started";
  if (raw.endsWith("-end")) return "job.completed";
  if (raw.endsWith("-error")) return "job.failed";
  if (raw === "integration_call") {
    const status = String(event?.status || "").toLowerCase();
    return status === "success" ? "integration.callSucceeded" : "integration.callFailed";
  }
  return (
    getEventType(rawEventType) ||
    (typeof rawEventType === "string" && rawEventType.trim() ? rawEventType.trim() : undefined)
  );
};

/** Recover the original occurrence time, never the time a legacy row was healed. */
const getEventTimestamp = (event: Record<string, any>): string | undefined => {
  const rowKey = String(event.eventRowKey || event.rowKey || event.RowKey || event.id || "").split("|")[0];
  const compact = rowKey.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(?:\.(\d+))?Z$/);
  const rowTimestamp = compact
    ? `${compact[1]}-${compact[2]}-${compact[3]}T${compact[4]}:${compact[5]}:${compact[6]}.${(compact[7] || "").padEnd(3, "0").slice(0, 3)}Z`
    : rowKey;
  for (const value of [event.timestamp, rowTimestamp]) {
    if (typeof value !== "string" || !value.trim()) continue;
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.toISOString();
  }
  return undefined;
};

const getEventMessage = (event: Record<string, any>): string | undefined => {
  const candidates = getEventObjectCandidates(event);
  return [
    ...candidates.map(candidate => candidate.message),
    event.notification?.body,
    event.notification?.summary,
    event.notification?.title,
    ...(event.facts || []).filter(isMessageFact)
      .map((fact: EventFact) => fact.value),
  ].find(value => typeof value === "string" && value.trim()) as string | undefined;
};

export const normalizeEventFields = (event: Record<string, any> | undefined | null): Partial<Event> => {
  const rawEventType = getRawEventType(event);
  const eventType = getStatusAwareEventType(event, rawEventType);
  const modelId = getEventModelId(event);
  const sessionId = getEventSessionId(event);
  const releaseId = event?.releaseId || event?.payload?.releaseId || event?.message?.releaseId;
  const facts = normalizeEventFacts(event?.facts);
  const requestId = event?.requestId;
  const meta = {
    ...(event?.meta && typeof event.meta === "object" ? event.meta : {}),
    ...(event?.severity ? { legacySeverity: event.severity } : {}),
    ...(event?.messageType ? { legacyMessageType: event.messageType } : {}),
    ...(event?.event && event.event !== eventType ? { legacyEvent: event.event } : {}),
    ...(event?.payload !== undefined ? { payload: event.payload } : {}),
    ...(event?.data !== undefined ? { data: event.data } : {}),
    ...(event?.message !== undefined && typeof event.message !== "string" ? { message: event.message } : {}),
  };
  delete meta.notification;
  // Legacy job lifecycle audit rows (<jobType>-start / -end) are routine: treat them as debug events.
  const status = eventType === "job.started" || (eventType === "job.completed" && normalizeStatus(event) !== "error")
    ? "debug"
    : normalizeStatus(event);
  // contact.updated events stored before metaFormat was recorded hold a diff (they now hold the update as sent).
  const metaFormat: EventMetaFormat =
    event?.metaFormat === "diff" || (!event?.metaFormat && event?.eventType === "contact.updated") ? "diff" : "normal";
  return {
    ...(eventType ? { eventType } : {}),
    ...(modelId ? { modelId } : {}),
    ...(sessionId ? { sessionId } : {}),
    ...(typeof releaseId === "string" && releaseId.trim() ? { releaseId } : {}),
    ...(event?.correlationId ? { correlationId: event.correlationId } : {}),
    ...(status ? { status } : {}),
    ...(facts ? { facts } : {}),
    ...(requestId ? { requestId } : {}),
    ...(event?.taskId ? { taskId: event.taskId } : {}),
    ...(event?.resolution ? { resolution: event.resolution } : {}),
    timestamp: getEventTimestamp(event || {}),
    message: getEventMessage(event || {}),
    ...(Object.keys(meta).length ? { meta } : {}),
    metaFormat,
  };
};

export const normalizeEvent = (event: Record<string, any> | undefined | null): Event | undefined => {
  if (!event) return undefined;
  const { title: _title, summary: _summary, ...notification } = event.notification || {};
  return {
    id: event.id,
    eventRowKey: event.eventRowKey,
    timestamp: event.timestamp,
    ...(event.identity ? { identity: event.identity } : {}),
    notification: {
      ...notification,
      facts: normalizeEventFacts(notification.facts),
      body: typeof notification.body === "string" && notification.body.trim() ? notification.body : getEventMessage(event) || getEventTypeInfo(getRawEventType(event))?.name || "Event recorded",
    },
    ...normalizeEventFields(event),
  };
};
